import React, { useEffect, useMemo, useState } from 'react';

import { changePassword, updateProfile } from './api';


const initialPasswordState = {
    old_password: '',
    new_password: '',
    confirm_password: '',
};


function buildInitialForm(user) {
    return {
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || '',
        phone_number: user?.phone_number || '',
        telegram_id: user?.telegram_id || '',
    };
}


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


export default function ProfilePage({ user, onProfileUpdated }) {
    const [form, setForm] = useState(buildInitialForm(user));
    const [avatarFile, setAvatarFile] = useState(null);
    const [profileState, setProfileState] = useState({ loading: false, error: '', success: '' });
    const [passwordForm, setPasswordForm] = useState(initialPasswordState);
    const [passwordState, setPasswordState] = useState({ loading: false, error: '', success: '' });
    const [avatarBroken, setAvatarBroken] = useState(false);
    const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');

    useEffect(() => {
        setForm(buildInitialForm(user));
        setAvatarBroken(false);
    }, [user]);

    useEffect(() => {
        if (!avatarFile) {
            setAvatarPreviewUrl('');
            return undefined;
        }

        const objectUrl = URL.createObjectURL(avatarFile);
        setAvatarPreviewUrl(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [avatarFile]);

    const displayName = useMemo(() => {
        const fullName = `${form.first_name} ${form.last_name}`.trim();
        return fullName || user?.username || 'Пользователь';
    }, [form.first_name, form.last_name, user]);

    const accountMetrics = useMemo(() => ([
        {
            label: 'Логин',
            value: user?.username || 'Не указан',
        },
        {
            label: 'Рабочая зона',
            value: user?.location?.city || 'Не указана',
        },
        {
            label: 'Права доступа',
            value: String(user?.permissions?.length || 0),
        },
        {
            label: 'Связь',
            value: form.phone_number || form.email || 'Не заполнено',
        },
    ]), [form.email, form.phone_number, user]);

    const avatarStatus = avatarFile
        ? `Выбран файл: ${avatarFile.name}`
        : '';

    function updateField(name, value) {
        setForm((current) => ({
            ...current,
            [name]: value,
        }));
    }

    function resetProfileForm() {
        setForm(buildInitialForm(user));
        setAvatarFile(null);
        setProfileState({ loading: false, error: '', success: '' });
        setAvatarBroken(false);
    }

    function resetPasswordForm() {
        setPasswordForm(initialPasswordState);
        setPasswordState({ loading: false, error: '', success: '' });
    }

    async function handleProfileSubmit(event) {
        event.preventDefault();
        setProfileState({ loading: true, error: '', success: '' });

        const payload = new FormData();
        Object.entries(form).forEach(([key, value]) => payload.append(key, value));
        if (avatarFile) {
            payload.append('profile_picture', avatarFile);
        }

        try {
            const response = await updateProfile(payload);
            onProfileUpdated(response.user);
            setAvatarFile(null);
            setProfileState({ loading: false, error: '', success: 'Профиль обновлен.' });
        } catch (error) {
            setProfileState({
                loading: false,
                error: extractErrorMessage(error, 'Не удалось обновить профиль.'),
                success: '',
            });
        }
    }

    async function handlePasswordSubmit(event) {
        event.preventDefault();
        setPasswordState({ loading: true, error: '', success: '' });

        try {
            const response = await changePassword(passwordForm);
            onProfileUpdated(response.user);
            setPasswordForm(initialPasswordState);
            setPasswordState({ loading: false, error: '', success: 'Пароль обновлен.' });
        } catch (error) {
            setPasswordState({
                loading: false,
                error: extractErrorMessage(error, 'Не удалось сменить пароль.'),
                success: '',
            });
        }
    }

    return (
        <div className="shell-page">
            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact shell-pageHeaderCompact--profile">
                    <div className="shell-pageBandIntro">
                        <span className="shell-sectionEyebrow">Личный кабинет</span>
                        <h3>Профиль пользователя</h3>
                    </div>
                    <div className="shell-profileHero">
                        <div className="shell-profileAvatarWrap">
                            {avatarPreviewUrl ? (
                                <img
                                    className="shell-profileAvatar"
                                    src={avatarPreviewUrl}
                                    alt={displayName}
                                />
                            ) : user?.profile_picture_url && !avatarBroken ? (
                                <img
                                    className="shell-profileAvatar"
                                    src={user.profile_picture_url}
                                    alt={displayName}
                                    onError={() => setAvatarBroken(true)}
                                />
                            ) : (
                                <div className="shell-profileAvatar shell-profileAvatar--placeholder">
                                    {displayName.slice(0, 1).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="shell-profileHeroMeta">
                            <strong>{displayName}</strong>
                            <span>{user?.location?.city || 'Локация не указана'}</span>
                        </div>
                    </div>
                </div>

                <div className="shell-pageSummary shell-pageSummary--profileTop">
                    {accountMetrics.map((metric) => (
                        <article key={metric.label} className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                            <span>{metric.label}</span>
                            <strong>{metric.value}</strong>
                        </article>
                    ))}
                </div>
            </section>

            <section className="shell-grid shell-grid--profile">
                <form className="shell-searchForm shell-searchForm--profile" onSubmit={handleProfileSubmit}>
                    <div className="shell-sectionHeader shell-sectionHeader--nested shell-field--full">
                        <div>
                            <span className="shell-sectionEyebrow">Основное</span>
                            <h3>Контакты и данные</h3>
                        </div>
                    </div>

                    <label className="shell-field">
                        <span>Имя</span>
                        <input value={form.first_name} onChange={(event) => updateField('first_name', event.target.value)} />
                    </label>
                    <label className="shell-field">
                        <span>Фамилия</span>
                        <input value={form.last_name} onChange={(event) => updateField('last_name', event.target.value)} />
                    </label>
                    <label className="shell-field">
                        <span>Email</span>
                        <input type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} />
                    </label>
                    <label className="shell-field">
                        <span>Телефон</span>
                        <input value={form.phone_number} onChange={(event) => updateField('phone_number', event.target.value)} />
                    </label>
                    <label className="shell-field">
                        <span>Telegram ID</span>
                        <input value={form.telegram_id} onChange={(event) => updateField('telegram_id', event.target.value)} />
                    </label>

                    <label className="shell-field shell-field--full">
                        <span>Аватар</span>
                        <input type="file" accept="image/*" onChange={(event) => setAvatarFile(event.target.files?.[0] || null)} />
                        {avatarStatus ? <small className="shell-fieldHint">{avatarStatus}</small> : null}
                    </label>

                    {profileState.error ? <div className="shell-inlineMessage shell-inlineMessage--error">{profileState.error}</div> : null}
                    {profileState.success ? <div className="shell-inlineMessage">{profileState.success}</div> : null}

                    <div className="shell-searchActions">
                        <button type="submit" className="shell-actionButton shell-actionButton--primary" disabled={profileState.loading}>
                            {profileState.loading ? 'Сохраняем...' : 'Сохранить профиль'}
                        </button>
                        <button type="button" className="shell-actionButton shell-actionButton--secondary" onClick={resetProfileForm} disabled={profileState.loading}>
                            Сбросить изменения
                        </button>
                    </div>
                </form>

                <div className="shell-inlineStack">
                    <section className="shell-resultCard shell-resultCard--profileSummary">
                        <div className="shell-resultCardTop">
                            <div>
                                <h3 className="shell-resultTitle">Аккаунт и доступ</h3>
                            </div>
                        </div>

                        <div className="shell-resultMetaGrid shell-resultMetaGrid--profile">
                            <div className="shell-resultMetaItem">
                                <span>Полное имя</span>
                                <strong>{displayName}</strong>
                            </div>
                            <div className="shell-resultMetaItem">
                                <span>Логин</span>
                                <strong>{user?.username || '—'}</strong>
                            </div>
                            <div className="shell-resultMetaItem">
                                <span>Email</span>
                                <strong>{form.email || 'Не указан'}</strong>
                            </div>
                            <div className="shell-resultMetaItem">
                                <span>Телефон</span>
                                <strong>{form.phone_number || 'Не указан'}</strong>
                            </div>
                            <div className="shell-resultMetaItem">
                                <span>Telegram ID</span>
                                <strong>{form.telegram_id || 'Не указан'}</strong>
                            </div>
                            <div className="shell-resultMetaItem">
                                <span>Локация</span>
                                <strong>{user?.location?.city || 'Не указана'}</strong>
                            </div>
                        </div>
                    </section>

                    <section className="shell-resultCard shell-resultCard--profileSecurity">
                        <div className="shell-resultCardTop">
                            <div>
                                <h3 className="shell-resultTitle">Безопасность</h3>
                            </div>
                        </div>

                        <form className="shell-searchForm shell-searchForm--embedded shell-searchForm--profilePassword" onSubmit={handlePasswordSubmit}>
                            <label className="shell-field">
                                <span>Текущий пароль</span>
                                <input
                                    type="password"
                                    value={passwordForm.old_password}
                                    onChange={(event) => setPasswordForm((current) => ({ ...current, old_password: event.target.value }))}
                                />
                            </label>
                            <label className="shell-field">
                                <span>Новый пароль</span>
                                <input
                                    type="password"
                                    value={passwordForm.new_password}
                                    onChange={(event) => setPasswordForm((current) => ({ ...current, new_password: event.target.value }))}
                                />
                            </label>
                            <label className="shell-field">
                                <span>Подтверждение</span>
                                <input
                                    type="password"
                                    value={passwordForm.confirm_password}
                                    onChange={(event) => setPasswordForm((current) => ({ ...current, confirm_password: event.target.value }))}
                                />
                            </label>

                            {passwordState.error ? <div className="shell-inlineMessage shell-inlineMessage--error">{passwordState.error}</div> : null}
                            {passwordState.success ? <div className="shell-inlineMessage">{passwordState.success}</div> : null}

                            <div className="shell-searchActions">
                                <button type="submit" className="shell-actionButton shell-actionButton--secondary" disabled={passwordState.loading}>
                                    {passwordState.loading ? 'Обновляем...' : 'Сменить пароль'}
                                </button>
                                <button type="button" className="shell-actionButton shell-actionButton--secondary" onClick={resetPasswordForm} disabled={passwordState.loading}>
                                    Очистить поля
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            </section>
        </div>
    );
}
